/**
 * Page de campagne publicitaire (`/offre/:slug`) — référentiel des choix du
 * questionnaire et envoi du prospect.
 *
 * Même convention que `src/lib/api-client.ts` : chemin relatif fixe
 * `/api/v1`, jamais de variable d'environnement (nginx proxy `/api/*` vers
 * l'API NestJS en production, Vite fait de même en dev).
 *
 * Les communes et leurs frais ne sont PAS codés ici : ils viennent de
 * `GET /leads/zones` (source unique côté API, `lead/campaign-zones.ts`).
 * Seules les réponses DISQUALIFIANTES vivent dans ce fichier, et c'est
 * volontaire : elles n'ont aucune valeur acceptée par l'API, puisque leur
 * rôle est justement d'arrêter le parcours ici, sans jamais faire arriver le
 * visiteur sur le WhatsApp du vendeur.
 */

const API_PREFIX = "/api/v1";

export interface CampaignZone {
  code: string;
  label: string;
  /** Frais de livraison en FCFA, `null` quand ils dépendent d'une info manquante. */
  feeMinor: number | null;
}

export interface CampaignChoice {
  code: string;
  label: string;
}

export interface CampaignChoices {
  zones: CampaignZone[];
  timings: CampaignChoice[];
  priceAnswers: CampaignChoice[];
}

/**
 * Codes sentinelles des réponses qui ferment le parcours. Ils ne sont jamais
 * transmis à l'API : `OffrePage` les intercepte et affiche un message de
 * sortie poli à la place.
 */
export const BLOCKING_ZONE_CODE = "__hors-abidjan";
export const BLOCKING_TIMING_CODE = "__je-me-renseigne";
export const BLOCKING_PRICE_CODE = "__trop-cher";

export const BLOCKING_ZONE_LABEL = "Je ne peux pas être livré(e) à Abidjan";
export const BLOCKING_TIMING_LABEL = "Je me renseigne seulement, je n'achète pas maintenant";
export const BLOCKING_PRICE_LABEL = "Non, c'est trop cher pour moi";

export function isBlockingCode(code: string): boolean {
  return code.startsWith("__");
}

export async function getCampaignChoices(): Promise<CampaignChoices> {
  const res = await fetch(`${API_PREFIX}/leads/zones`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(`Échec de la requête API VVS : GET /leads/zones (HTTP ${res.status})`);
  }
  return (await res.json()) as CampaignChoices;
}

export interface QuizLeadPayload {
  productSlug: string;
  fullName: string;
  phone: string;
  zoneCode: string;
  timing: string;
  priceAnswer: string;
  source?: string;
}

export interface QuizLeadResult {
  /** Lien `wa.me` pré-rempli : le client ouvre lui-même la conversation. */
  whatsappLink: string;
  /** Indique si l'alerte Evolution API est partie. Jamais bloquant pour le client. */
  sellerNotified: boolean;
}

export async function submitQuizLead(payload: QuizLeadPayload): Promise<QuizLeadResult> {
  const res = await fetch(`${API_PREFIX}/leads/quiz`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    let detail: string | undefined;
    try {
      const problem = (await res.json()) as { detail?: string; title?: string };
      detail = problem.detail ?? problem.title;
    } catch {
      detail = undefined;
    }
    throw new Error(detail ?? `Envoi impossible pour le moment (HTTP ${res.status}).`);
  }

  return (await res.json()) as QuizLeadResult;
}
